import { useState, useEffect } from "react";
import { Col, Form } from "react-bootstrap";
import { BsChevronDown, BsChevronRight } from 'react-icons/bs';
import AnimatedSelect from "../../../shared/AnimatedSelect";
import "../../../shared/AnimatedSelect.css";

const ContactFilters = ({filterValues = {}, campaign, journeys, lists, features, landingpage, region, state, city, onFilterChange}) => {
  const [selectedFilter, setSelectedFilter] = useState(filterValues.mainFilter || "");
  const [subFilter, setSubFilter] = useState(filterValues.subFilter || "");

  // Keep local UI state in sync when parent provides prefilled values
  useEffect(() => {
    setSelectedFilter(filterValues.mainFilter || "");
  }, [filterValues.mainFilter]);

  useEffect(() => {
    setSubFilter(filterValues.subFilter || "");
  }, [filterValues.subFilter]);

  // Default selection for main and sub filters when not provided
  useEffect(() => {
    if (!filterValues.mainFilter) {
      setSelectedFilter("Contact Source");
      onFilterChange && onFilterChange("mainFilter", "Contact Source");
    }
  }, [filterValues.mainFilter, onFilterChange]);

  useEffect(() => {
    if ((selectedFilter || filterValues.mainFilter) === "Contact Source" && !filterValues.subFilter) {
      setSubFilter("Imported (CSV / Bulk Upload)");
      onFilterChange && onFilterChange("subFilter", "Imported (CSV / Bulk Upload)");
    }
  }, [selectedFilter, filterValues.mainFilter, filterValues.subFilter, onFilterChange]);

  // Main Filters for Contacts
  const filterOptions = {
    "Contact Source": [
      "Imported (CSV / Bulk Upload)",
      "Added Manually",
      "Synced from Leads",
      "Referral",
    ],
    // Removed "Location" here because it will directly open 4 dropdowns
    "Contact Demographics": [],
    "Contact Activity": [
      "Contact Created On",
      "Last Updated On",
      "Last Interaction Date"
    ],
    "Contact Lifecycle Stage": [
      "Prospect",
      "Active Customer",
      "Dormant / Inactive",
      "Converted to Lead",
      "Blocked / Unsubscribed",
    ],
    "Contact Preferences": [
      "Preferred Communication Channel",
      "Having Email"
    ],
  };

  // Sub-options for dropdowns
  const campaignTypes = ["Email", "WhatsApp", "Push"];

  return (
    <>
      <Col className="custom_col">
        <AnimatedSelect
          value={filterValues.hasDone || "HasDone"}
          options={[
            { label: "Has Done", value: "HasDone" },
            { label: "Has Not Done", value: "HasNotDone" }
          ]}
          onChange={(value) => onFilterChange && onFilterChange("hasDone", value)}
        />
      </Col>

      {/* Main Filter Select */}
      <Col className="custom_col">
        <AnimatedSelect
          value={selectedFilter}
          options={[
            { label: "Select Contact By", value: "" },
            ...Object.keys(filterOptions).map((main) => ({
              label: main,
              value: main
            }))
          ]}
          onChange={(value) => {
            setSelectedFilter(value);
            setSubFilter("");
            onFilterChange && onFilterChange("mainFilter", value);
          }}
        />
      </Col>

      {/* Sub Filter Select (only when filter has inner options) */}
      {selectedFilter && filterOptions[selectedFilter].length > 0 && (
        <Col className="custom_col">
          <AnimatedSelect
            value={subFilter}
            options={[
              { label: "Select Sub Filter", value: "" },
              ...filterOptions[selectedFilter].map((sub) => ({
                label: sub,
                value: sub
              }))
            ]}
            onChange={(value) => {
              setSubFilter(value);
              onFilterChange && onFilterChange("subFilter", value);
            }}
          />
        </Col>
      )}

      {/* ============ CONDITIONAL FIELDS ============ */}

      {/* CONTACT DEMOGRAPHICS */}
      {selectedFilter === "Contact Demographics" && (
        <>
          <Col className="custom_col">
            <AnimatedSelect
              value={filterValues.contactZone || ""}
              options={[
                { label: "Select Zone", value: "" },
                ...region.map((item) => ({
                  label: item.name,
                  value: item.id
                }))
              ]}
              onChange={(value) => onFilterChange && onFilterChange("contactZone", value)}
            />
          </Col>
          <Col className="custom_col">
            <AnimatedSelect
              value={filterValues.contactState || ""}
              options={[
                { label: "Select State", value: "" },
                ...state.map((item) => ({
                  label: item.name,
                  value: item.id
                }))
              ]}
              onChange={(value) => onFilterChange && onFilterChange("contactState", value)}
            />
          </Col>
          <Col className="custom_col">
            <AnimatedSelect
              multiple
              value={filterValues.contactCity || []}
              options={[
                ...city.map((item) => ({
                  label: item.name,
                  value: item.id
                }))
              ]}
              placeholder="Select Cities"
              onChange={(value) => onFilterChange && onFilterChange("contactCity", value)}
            />
          </Col>
          <Col className="custom_col">
            <Form.Control 
              type="text" 
              placeholder="Enter Pincode"
              value={filterValues.contactPincode || ""}
              onChange={(e) => onFilterChange && onFilterChange("contactPincode", e.target.value)}
            />
          </Col>
        </>
      )}

      {/* CONTACT ACTIVITY */}
      {selectedFilter === "Contact Activity" &&
        ["Contact Created On", "Last Updated On", "Last Interaction Date"].includes(
          subFilter
        ) && (
          <>
            <Col className="custom_col">
              <Form.Control 
                type="date" 
                placeholder="Start Date"
                value={filterValues.activityStartDate || ""}
                onChange={(e) => onFilterChange && onFilterChange("activityStartDate", e.target.value)}
              />
            </Col>
            <Col className="custom_col">
              <Form.Control 
                type="date" 
                placeholder="End Date"
                value={filterValues.activityEndDate || ""}
                onChange={(e) => onFilterChange && onFilterChange("activityEndDate", e.target.value)}
              />
            </Col>
          </>
        )}

      {/* CONTACT PREFERENCES */}
      {selectedFilter === "Contact Preferences" &&
        subFilter === "Preferred Communication Channel" && (
          <Col className="custom_col">
            <AnimatedSelect
              value={filterValues.preferredChannel || ""}
              options={[
                { label: "Select Channel", value: "" },
                ...campaignTypes.map((c) => ({ label: c, value: c }))
              ]}
              onChange={(value) => onFilterChange && onFilterChange("preferredChannel", value)}
            />
          </Col>
        )}
    </>
  );
};

export default ContactFilters;

// Section wrapper for Contacts (default form + applied filters)
export const ContactsSection = ({
  dynamicFilters = [],
  renderFilterContent,
  renderContactFilters,
  onResetDefault,
  onAddFilter,
  showDefault = true
}) => {
  // State to track expanded section
  const [isExpanded, setIsExpanded] = useState(true);

  // Group filters by type (Contacts)
  const contactsFilters = dynamicFilters.filter(filter => filter.type === "Contacts");
  const count = contactsFilters.length;

  return (
    <div className="mb-3 ps-3">
      <div className="filter-sections-wrapper">
        <div className="filter-section-container">
          <div 
            className="filter-section-header"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="filter-section-title">
              {isExpanded ? (
                <BsChevronDown className="chevron-icon" />
              ) : (
                <BsChevronRight className="chevron-icon" />
              )}
              <span>Contacts</span>
              {count > 0 && (
                <span className="filter-section-count">({count})</span>
              )}
            </div>
            <button
              className="filter-section-add-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (onAddFilter) {
                  onAddFilter();
                  // Expand section when adding a new filter
                  if (!isExpanded) {
                    setIsExpanded(true);
                  }
                }
              }}
              type="button"
            >
              +
            </button>
          </div>
          {isExpanded && (
            <div className="filter-section-content">
              {/* Default Contact filters section - toggled by showDefault */}
              {showDefault && (
                <div className="filter-row">
                  <div className="row row-cols-auto text-center ms-2">
                    {renderContactFilters && renderContactFilters()}
                    <div className="col custom_col">
                      <button 
                        type="button" 
                        className="ms-2 btn btn-outline-danger btn-sm py-0 px-1"
                        onClick={onResetDefault}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Applied Filters Section */}
              {contactsFilters.map((filter) => (
                <div key={filter.id} className="filter-row">
                  {renderFilterContent && renderFilterContent(filter.type, filter.id)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Build dynamic Contacts filters from API response array
// Each item should resemble the UI fields used by ContactFilters
export function buildContactsDynamicFilters(contactData = []) {
  const nextDynamic = [];
  if (!Array.isArray(contactData)) return nextDynamic;

  contactData.forEach((c, idx) => {
    // If data already in UI shape, pass-through
    if (!c.action) {
      const uiContacts = {
        hasDone: c?.hasDone || 'HasDone',
        mainFilter: c?.mainFilter || '',
        subFilter: c?.subFilter || '',
        contactZone: c?.contactZone || '',
        contactState: c?.contactState || '',
        contactCity: c?.contactCity || '',
        contactPincode: c?.contactPincode || '',
        channel: c?.channel || '',
        preferredChannel: c?.preferredChannel || '',
        hasEmail: c?.hasEmail || '',
        activityDate: c?.activityDate || '',
      };
      nextDynamic.push({ id: Date.now() + 8000 + idx, type: 'Contacts', name: 'Contacts', filters: uiContacts });
      return;
    }

    // Map from backend contact filters
    const operator = String(c.operator || '').toLowerCase();
    const hasDone = operator.includes('not') ? 'HasNotDone' : 'HasDone';
    const action = String(c.action || '').toLowerCase();
    const cond = c.condition || {};

    const ui = {
      hasDone,
      mainFilter: '',
      subFilter: '',
      contactZone: '',
      contactState: '',
      contactCity: '',
      contactPincode: '',
      preferredChannel: '',
      activityStartDate: '',
      activityEndDate: '',
    };

    if (action === 'contact_source') {
      ui.mainFilter = 'Contact Source';
      const value = String(cond.value || '').toLowerCase();
      if (value === 'imported') ui.subFilter = 'Imported (CSV / Bulk Upload)';
      else if (value === 'manual') ui.subFilter = 'Added Manually';
      else if (value === 'synced_from_leads') ui.subFilter = 'Synced from Leads';
      else if (value === 'referral') ui.subFilter = 'Referral';
    } else if (action === 'contact_demographics') {
      ui.mainFilter = 'Contact Demographics';
      if (cond.zone) ui.contactZone = String(cond.zone);
      if (cond.state) ui.contactState = String(cond.state);
      if (cond.city) ui.contactCity = Array.isArray(cond.city) ? cond.city.map(String) : [String(cond.city)];
      if (cond.pincode) ui.contactPincode = String(cond.pincode);
    } else if (action === 'contact_activity') {
      ui.mainFilter = 'Contact Activity';
      const activity = String(cond.activity || '');
      if (activity === 'created_on') ui.subFilter = 'Contact Created On';
      else if (activity === 'last_updated_on') ui.subFilter = 'Last Updated On';
      else if (activity === 'last_interaction_on') ui.subFilter = 'Last Interaction Date';
      // Support both new start/end and legacy single value
      ui.activityStartDate = cond.start_date || cond.value || '';
      ui.activityEndDate = cond.end_date || '';
    } else if (action === 'contact_lifecycle_stage') {
      ui.mainFilter = 'Contact Lifecycle Stage';
      const v = String(cond.value || '').toLowerCase();
      if (v === 'prospect') ui.subFilter = 'Prospect';
      else if (v === 'active') ui.subFilter = 'Active Customer';
      else if (v === 'dormant_inactive') ui.subFilter = 'Dormant / Inactive';
      else if (v === 'converted_to_lead') ui.subFilter = 'Converted to Lead';
      else if (v === 'blocked' || v === 'unsubscribed' || v === 'blocked_unsubscribed') ui.subFilter = 'Blocked / Unsubscribed';
    } else if (action === 'contact_preferences') {
      ui.mainFilter = 'Contact Preferences';
      const source = String(cond.source || '');
      if (source === 'channel') {
        ui.subFilter = 'Preferred Communication Channel';
        ui.preferredChannel = cond.value || '';
      } else if (source === 'having_email') {
        ui.subFilter = 'Having Email';
      }
    }

    nextDynamic.push({ id: Date.now() + 8000 + idx, type: 'Contacts', name: 'Contacts', filters: ui });
  });

  return nextDynamic;
}

// Process contact filters for payload building
export function processContactFilters(contactValues, payload) {
  if (!contactValues || Object.keys(contactValues).length === 0) return;

  const operator = contactValues.hasDone === "HasNotDone" ? "has not done" : "has done";
  const operatorUnderscored = operator.replace(/\s+/g, "_");

  // Maps to remove duplicacy and normalize values
  const CONTACT_SOURCE_MAP = {
    "imported (csv / bulk upload)": "imported",
    "imported": "imported",
    "added manually": "manual",
    "manual": "manual",
    "synced from leads": "synced_from_leads",
    "synced_from_leads": "synced_from_leads",
    "referral": "referral",
  };
  const CONTACT_ACTIVITY_KEY_MAP = {
    "Contact Created On": "created_on",
    "Last Updated On": "last_updated_on",
    "Last Interaction Date": "last_interaction_on",
  };
  const LIFECYCLE_VALUE_MAP = {
    "Prospect": "prospect",
    "Active Customer": "active",
    "Dormant / Inactive": "dormant_inactive",
    "Converted to Lead": "converted_to_lead",
    "Blocked / Unsubscribed": "blocked_unsubscribed",
  };

  // Contact Source
  if (contactValues.mainFilter === "Contact Source" && contactValues.subFilter) {
    const key = String(contactValues.subFilter || "").toLowerCase();
    const value = CONTACT_SOURCE_MAP[key] || "";

    payload.filter.contacts.push({
      operator,
      action: "contact_source",
      condition: { value }
    });
  }

  // Contact Demographics
  if (contactValues.mainFilter === "Contact Demographics") {
    const condition = {};
    if (contactValues.contactZone) condition.zone = Number(contactValues.contactZone);
    if (contactValues.contactState) condition.state = Number(contactValues.contactState);
    if (contactValues.contactCity) {
      condition.city = Array.isArray(contactValues.contactCity)
        ? contactValues.contactCity.map(Number)
        : [Number(contactValues.contactCity)];
    }
    if (contactValues.contactPincode) condition.pincode = String(contactValues.contactPincode);
    if (Object.keys(condition).length > 0) {
      payload.filter.contacts.push({ operator, action: "contact_demographics", condition });
    }
  }

  // Contact Activity
  if (contactValues.mainFilter === "Contact Activity" && contactValues.subFilter) {
    const activity = CONTACT_ACTIVITY_KEY_MAP[contactValues.subFilter] || "";
    const start = contactValues.activityStartDate || "";
    const end = contactValues.activityEndDate || "";
    if (activity && (start || end)) {
      payload.filter.contacts.push({
        operator: operator, // backend expects underscored form here
        action: "contact_activity",
        condition: {
          activity,
          ...(start ? { start_date: start } : {}),
          ...(end ? { end_date: end } : {})
        }
      });
    }
  }

  // Contact Lifecycle Stage
  if (contactValues.mainFilter === "Contact Lifecycle Stage" && contactValues.subFilter) {
    const backendValue = LIFECYCLE_VALUE_MAP[String(contactValues.subFilter)] || String(contactValues.subFilter).toLowerCase().replace(/\s+\/\s+/g, "_");
    payload.filter.contacts.push({ operator, action: "contact_lifecycle_stage", condition: { value: backendValue } });
  }

  // Contact Preferences
  if (contactValues.mainFilter === "Contact Preferences" && contactValues.subFilter) {
    if (contactValues.subFilter === "Preferred Communication Channel") {
      payload.filter.contacts.push({
        operator,
        action: "contact_preferences",
        condition: { source: "channel", value: contactValues.preferredChannel || "" }
      });
    } else if (contactValues.subFilter === "Having Email") {
      // No sub field; always send with source having_email and blank value
      payload.filter.contacts.push({
        operator,
        action: "contact_preferences",
        condition: { source: "having_email", value: "" }
      });
    }
  }
}